import {useEffect, useRef, useState, ChangeEvent} from 'react'
import {useParams} from 'react-router-dom'

import styles from './AMLManualCheck.module.scss'
import {
  Button,
  Form,
  FormItem,
  Text,
  Select,
  Box,
  File,
  useForm,
  useWatch,
} from 'common/components'
import {useUploadFile} from 'common/api'
import {FIELD_RULES} from 'common/constants'
import {Auth, useCheckAMLManual} from '..'

export default function AMLManualCheck() {
  const {token} = useParams()
  const {
    mutate: checkAMLManual,
    isMutating,
    isSuccess,
  } = useCheckAMLManual(token)
  const [identityFile, setIdentityFile] = useState<any>()
  const [visaFile, setVisaFile] = useState<any>()
  const [additionalFile, setAdditionalFile] = useState<any>()
  const [additionalVisaFile, setAdditionalVisaFile] = useState<any>()
  const [addressFile, setAddressFile] = useState<any>()
  const [form] = useForm()
  const identityVerificationValue = useWatch('identityVerification', form)
  const additionalVerificationValue = useWatch('additionalVerification', form)
  const addressVerificationValue = useWatch('addressVerification', form)

  useEffect(() => {
    setIdentityFile(null)
    setVisaFile(null)
    setAdditionalFile(null)
    setAdditionalVisaFile(null)
  }, [identityVerificationValue])

  useEffect(() => {
    setAdditionalFile(null)
    setAdditionalVisaFile(null)
  }, [additionalVerificationValue])

  useEffect(() => {
    setAddressFile(null)
  }, [addressVerificationValue])

  const handleFinish = () => {
    const identityDocuments = [
      identityFile?.uuid,
      visaFile?.uuid,
      additionalFile?.uuid,
      additionalVisaFile?.uuid,
    ].filter((uuid) => uuid)
    const addressDocuments = [addressFile?.uuid].filter((uuid) => uuid)
    const values = {
      identityDocuments,
      addressDocuments,
    }
    checkAMLManual(values)
  }

  const isSubmitAccessed =
    identityVerificationValue &&
    identityFile &&
    addressVerificationValue &&
    addressFile

  return (
    <Auth title={isSuccess ? undefined : 'AML Check'}>
      {isSuccess ? (
        <Box align="center">
          <Text size="4xl" mb={24}>
            Thank you!
          </Text>
          <Text maxInterval>
            We have received your AML documents through our submission form. Our
            team will review it, and we will contact you if further action is
            required.
          </Text>
        </Box>
      ) : (
        <Form className={styles.form} form={form} onFinish={handleFinish}>
          <Box>
            <Text size="lg" weight="demiBold" mb={6}>
              Identity verification
            </Text>
            <Text size="xs" color="grey" mb={16}>
              Please choose one of the identification options (must include the
              signature page)
            </Text>
            <FormItem name="identityVerification" rules={FIELD_RULES}>
              <Select
                placeholder="Please select"
                options={identityVerificationOptions}
              />
            </FormItem>
            {identityFile ? (
              <File
                uuid={identityFile.uuid}
                name={identityFile.blob.name}
                size={identityFile.blob.size}
                url={identityFile.url}
              />
            ) : identityVerificationValue ? (
              <UploadButton setFile={setIdentityFile} />
            ) : null}
          </Box>
          {identityVerificationValue === 'overseasPassport' ? (
            <Box mt={24}>
              <Text size="xs" color="grey" mb={12}>
                If an overseas document is provided for identity verificatopn
                and the person is residing in New Zealand, a cop of the visa
                issued by New Zealand Immigration is also required.
              </Text>
              {visaFile ? (
                <File
                  uuid={visaFile.uuid}
                  name={visaFile.blob.name}
                  size={visaFile.blob.size}
                  url={visaFile.url}
                />
              ) : (
                <UploadButton setFile={setVisaFile} />
              )}
            </Box>
          ) : identityVerificationValue === 'driverLicense' ? (
            <Box mt={24}>
              <Text size="xs" color="grey" mb={12}>
                Please provide copies of one of additional documents.
              </Text>
              <FormItem name="additionalVerification" rules={FIELD_RULES}>
                <Select
                  placeholder="Please select"
                  options={additionalVerificationOptions}
                />
              </FormItem>
              {additionalFile ? (
                <File
                  uuid={additionalFile.uuid}
                  name={additionalFile.blob.name}
                  size={additionalFile.blob.size}
                  url={additionalFile.url}
                />
              ) : additionalVerificationValue ? (
                <UploadButton setFile={setAdditionalFile} />
              ) : null}
              {(additionalVerificationValue === 'birthCertificate' ||
                additionalVerificationValue === 'citizenshipCertificate') && (
                <Box mt={24}>
                  <Text size="xs" color="grey" mb={12}>
                    Copy of the visa issued by NZ Immigration.
                  </Text>
                  {additionalVisaFile ? (
                    <File
                      uuid={additionalVisaFile.uuid}
                      name={additionalVisaFile.blob.name}
                      size={additionalVisaFile.blob.size}
                      url={additionalVisaFile.url}
                    />
                  ) : (
                    <UploadButton setFile={setAdditionalVisaFile} />
                  )}
                </Box>
              )}
            </Box>
          ) : null}
          <Box mt={40}>
            <Text size="lg" weight="demiBold" mb={6}>
              Address verification
            </Text>
            <Text size="xs" color="grey" mb={16}>
              Please choose one of the documents. The document must be addressed
              to the person at their home address and be no more than 12 months
              old.
            </Text>
            <FormItem name="addressVerification" rules={FIELD_RULES}>
              <Select
                placeholder="Please select"
                options={addressVerificationOptions}
              />
            </FormItem>
            {addressFile ? (
              <File
                uuid={addressFile.uuid}
                name={addressFile.blob.name}
                size={addressFile.blob.size}
                url={addressFile.url}
              />
            ) : addressVerificationValue ? (
              <UploadButton setFile={setAddressFile} />
            ) : null}
          </Box>
          <Box mt={40} mb={24}>
            <Button
              block
              htmlType="submit"
              disabled={!isSubmitAccessed}
              loading={isMutating}
            >
              Send documents
            </Button>
          </Box>
          <Text size="xs" color="grey">
            We will use your documents for other loans as long as they are
            valid.
          </Text>
        </Form>
      )}
    </Auth>
  )
}

const UploadButton = ({setFile}: any) => {
  const {mutate: uploadFile, isLoading} = useUploadFile({
    setFile: (file: any) => setFile(file),
  })

  const inputRef = useRef(null)

  const handleDownload = (e: ChangeEvent<HTMLInputElement>) => {
    uploadFile(e.target.files[0])
    // clear the input value, there is no React specific way to do that
    setTimeout(() => {
      e.target.value = null
    })
  }

  const handleClick = () => inputRef.current.click()

  return (
    <>
      <Button variant="secondary" loading={isLoading} onClick={handleClick}>
        add file
      </Button>
      <input
        multiple
        type="file"
        ref={inputRef}
        style={uploadInputStyle}
        onChange={handleDownload}
      />
    </>
  )
}

const uploadInputStyle = {
  display: 'none',
}

const identityVerificationOptions = [
  {value: 'nzPassport', label: 'NZ passport'},
  {value: 'nzCertificate', label: 'NZ certificate of identity'},
  {value: 'nzRefugeeTravel', label: 'NZ refugee travel document'},
  {value: 'emergencyTravel', label: 'Emergency travel document'},
  {value: 'nzFirearms', label: 'NZ firearms licence'},
  {
    value: 'overseasPassport',
    label: (
      <>
        <p>Overseas passport</p>
        <Text size="xs" color="grey">
          And copy of the visa issued by NZ Immigration
        </Text>
      </>
    ),
  },
  {value: 'identityCard', label: 'National identity card'},
  {
    value: 'driverLicense',
    label: (
      <>
        <p>NZ driver licence</p>
        <Text size="xs" color="grey">
          And one of additional documents
        </Text>
      </>
    ),
  },
]

const addressVerificationOptions = [
  {value: 'utilityBill', label: 'Utility bill'},
  {value: 'ratesBill', label: 'Rates bill'},
  {value: 'bankAccount', label: 'Bank account statement'},
  {value: 'government', label: 'Government document'},
  {value: 'localCouncil', label: 'Local council notofocation/demand'},
  {value: 'rentalTenancy', label: 'Rental tenancy agreement'},
  {
    value: 'nonBankStatement',
    label: 'Non-bank financial institution statement',
  },
  {value: 'ird', label: 'IRD tax invoice/certificate'},
  {value: 'electoralPapers', label: 'Electoral roll papers'},
  {value: 'insurancePolicy', label: 'Insurance policy document'},
  {value: 'carRegistration', label: 'Car registration/demand'},
]

const additionalVerificationOptions = [
  {value: 'bankCard', label: 'Embossed bank card'},
  {value: 'bankStatement', label: 'Bank statement'},
  {
    value: 'birthCertificate',
    label: (
      <>
        <p>NZ/overseas birth certificate</p>
        <Text size="xs" color="grey">
          And copy of the visa issued by NZ Immigration
        </Text>
      </>
    ),
  },
  {
    value: 'citizenshipCertificate',
    label: (
      <>
        <p>NZ/overseas citizenship certificate</p>
        <Text size="xs" color="grey">
          And copy of the visa issued by NZ Immigration
        </Text>
      </>
    ),
  },
  {value: 'marriageCertificate', label: 'NZ marriage certificate'},
  {
    value: 'governmentStatement',
    label: 'Statement issued by a government agency',
  },
  {value: 'superCard', label: 'SuperGold card'},
]
